import { defineStore } from 'pinia'
import { ref } from 'vue'

import { parseCallableError } from '@/lib/firebase/errors'
import {
  connectSocialAccount as repoConnect,
  disconnectSocialAccount as repoDisconnect,
  getSocialAccount as repoGetAccount,
} from '@/repositories/connections'
import { failed, loaded, loading, type LoadState } from '@/types/load-state'
import type { SocialAccount, SocialPlatform } from '@/types/connections'

interface PlatformState {
  account: LoadState<SocialAccount | null>
  actionStatus: 'idle' | 'connecting' | 'disconnecting' | 'error'
  error: string | null
}

export const useConnectionsStore = defineStore('connections', () => {
  const linkedin = ref<PlatformState>({
    account: loading(),
    actionStatus: 'idle',
    error: null,
  })

  const whatsapp = ref<PlatformState>({
    account: loading(),
    actionStatus: 'idle',
    error: null,
  })

  function getPlatformState(platform: SocialPlatform): PlatformState {
    return platform === 'linkedin' ? linkedin.value : whatsapp.value
  }

  async function load(platform: SocialPlatform): Promise<void> {
    const state = getPlatformState(platform)
    state.account = loading()
    state.error = null

    try {
      const account = await repoGetAccount(platform)
      state.account = loaded(account)
    } catch (cause) {
      state.account = failed(parseCallableError(cause).message)
      state.error = parseCallableError(cause).message
    }
  }

  async function loadAll(): Promise<void> {
    await Promise.all([load('linkedin'), load('whatsapp')])
  }

  async function connect(platform: SocialPlatform, brandName?: string): Promise<void> {
    const state = getPlatformState(platform)
    state.actionStatus = 'connecting'
    state.error = null

    try {
      const { authUrl } = await repoConnect(platform, brandName)
      window.location.assign(authUrl)
    } catch (cause) {
      state.actionStatus = 'error'
      const error = parseCallableError(cause)
      state.error = error.message
    }
  }

  async function disconnect(platform: SocialPlatform): Promise<void> {
    const state = getPlatformState(platform)
    state.actionStatus = 'disconnecting'
    state.error = null

    try {
      await repoDisconnect(platform)
      await load(platform)
      state.actionStatus = 'idle'
    } catch (cause) {
      state.actionStatus = 'error'
      const error = parseCallableError(cause)
      state.error = error.message
    }
  }

  return {
    linkedin,
    whatsapp,
    load,
    loadAll,
    connect,
    disconnect,
  }
})
